import { AsyncPipe, Location } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApiDeviceGroupRequestDto,
  ApiDeviceGroupResponseDto,
  ApiDeviceHistoryEntryResponseDto,
  DeviceGroupState,
  DeviceGroupsApiService,
} from '@birco/rainmesh-api';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { NotFoundComponent } from '../../../../shared/components/error/not-found/not-found.component';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { HistoryComponent } from '../../components/history/history.component';
import { HistoryGroupViewModel } from '../../models/history-view-model.interface';
import { DeviceGroupActions } from '../../state/device-groups/device-group.actions';
import {
  selectDeviceGroupById,
  selectIsDeviceGroupsLoading,
  selectSortedDevicesByGroupId,
} from '../../state/device-groups/device-group.selectors';
import { DeviceResponseDto } from '../../models/device-response.interface';
import { RuleViewModel } from '../../../rules/models/rule-view-model.interface';
import { selectRulesByDeviceGroupIdAsViewModel } from '../../../rules/state/rule.selectors';
import { DeviceItemCardComponent } from '../../components/device-item-card/device-item-card.component';
import { DeviceRulesCardComponent } from '../../components/device-rules-card/device-rules-card.component';
import {
  ContextMenuComponent,
  ContextMenuData,
} from '../../../../shared/components/context-menu/context-menu.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogData,
  ConfirmDialogComponent,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  DialogUpdateDeviceGroupComponent,
  DeviceGroupDialogResult,
} from '../../components/dialog-update-device-group/dialog-update-device-group.component';
import { MapWithLocationComponent } from '../../../map/components/map-with-location/map-with-location.component';
import { MapDevice } from '../../../map/models/map-device.interface';
import { RuleActions } from '../../../rules/state/rule.actions';
import { LabelConverter } from '../../../../shared/converter/label.converter';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { isDeviceActive } from '../../utils/device-value.utils';

@Component({
  selector: 'app-page-device-group-detail',
  imports: [
    CardComponent,
    IconComponent,
    TranslatePipe,
    AsyncPipe,
    HistoryComponent,
    NotFoundComponent,
    DeviceItemCardComponent,
    DeviceRulesCardComponent,
    ContextMenuComponent,
    MapWithLocationComponent,
    TagComponent,
  ],
  templateUrl: './page-device-group-detail.component.html',
  styleUrl: './page-device-group-detail.component.scss',
})
export class PageDeviceGroupDetailComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly deviceGroupsApiService = inject(DeviceGroupsApiService);
  protected readonly LabelConverter = LabelConverter;

  readonly loading$ = this.store.select(selectIsDeviceGroupsLoading);

  private readonly pageSize = 20;
  private currentPage = 0;
  private currentGroupId?: string;
  private currentDeviceGroup?: ApiDeviceGroupResponseDto | null;
  private historyRequestSeq = 0;

  history: HistoryGroupViewModel[] = [];
  historyLoading = false;
  canLoadMoreHistory = false;

  private readonly groupId$ = this.route.paramMap.pipe(
    map((params) => params.get('id')),
    filter((id): id is string => !!id),
    distinctUntilChanged(),
  );

  readonly deviceGroup$ = this.groupId$.pipe(
    switchMap((id) => this.store.select(selectDeviceGroupById(id))),
  );

  ruleList$?: Observable<RuleViewModel[]>;
  deviceList$ = this.groupId$.pipe(
    switchMap((id) => this.store.select(selectSortedDevicesByGroupId(id))),
  );
  mapDeviceList$ = this.deviceList$.pipe(
    map((devices) => (devices ?? []).map((device) => this.toMapDevice(device))),
  );

  contextMenu: ContextMenuData[] = [
    {
      labelKey: 'deviceGroups.trigger.active',
      action: () => this.onTriggerAction(true),
    },
    {
      labelKey: 'deviceGroups.trigger.inactive',
      action: () => this.onTriggerAction(false),
    },
    {
      labelKey: 'default.delete',
      action: () => this.onDelete(),
    },
  ];

  constructor() {
    this.groupId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      this.store.dispatch(
        DeviceGroupActions.public.loadDeviceGroupById({ id }),
      );
      this.store.dispatch(
        RuleActions.public.loadAllRulesByFilters({ filter: {} }),
      );
      this.currentGroupId = id;
      this.loadInitialHistory();
    });

    this.deviceGroup$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deviceGroup) => {
        this.currentDeviceGroup = deviceGroup;
      });

    this.ruleList$ = this.groupId$.pipe(
      switchMap((id) =>
        this.store.select(selectRulesByDeviceGroupIdAsViewModel(id)),
      ),
      catchError((_) => EMPTY),
    );
  }

  onTriggerAction(value: boolean) {
    if (!this.currentGroupId) return;
    this.store.dispatch(
      DeviceGroupActions.public.triggerDeviceGroupAction({
        id: this.currentGroupId,
        value: value,
      }),
    );
  }

  onEdit() {
    this.deviceGroup$.pipe(take(1)).subscribe((deviceGroup) => {
      if (!deviceGroup) return;

      const dialogRef = this.dialog.open(DialogUpdateDeviceGroupComponent, {
        width: '36rem',
        data: { mode: 'update', deviceGroup },
      });

      dialogRef
        .afterClosed()
        .pipe(
          take(1),
          filter(
            (result): result is DeviceGroupDialogResult =>
              !!result && !!result.id,
          ),
        )
        .subscribe((result) => {
          this.store.dispatch(
            DeviceGroupActions.public.updateDeviceGroup({
              id: result.id!,
              requestDto: result.requestDto,
            }),
          );
        });
    });
  }

  onDelete() {
    this.deviceGroup$.pipe(take(1)).subscribe((deviceGroup) => {
      if (!deviceGroup) return;

      const dialogData: ConfirmDialogData = {
        title: this.translateService.instant('deviceGroups.delete.title'),
        body: this.translateService.instant('deviceGroups.delete.body'),
        action: 'danger',
        confirmLabel: this.translateService.instant('default.delete'),
      };

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: dialogData,
      });
      dialogRef
        .afterClosed()
        .pipe(
          take(1),
          filter((result): result is true => result === true),
        )
        .subscribe(() => {
          this.store.dispatch(
            DeviceGroupActions.public.deleteDeviceGroupById({
              id: deviceGroup.id,
            }),
          );
        });
    });
  }

  onToggleFavorite(deviceGroup: ApiDeviceGroupResponseDto): void {
    this.store.dispatch(
      DeviceGroupActions.public.updateDeviceGroup({
        id: deviceGroup.id,
        requestDto: {
          name: deviceGroup.name,
          deviceIds: deviceGroup.devices.map((device) => device.id),
          isFavorite: !deviceGroup.isFavorite,
        },
      }),
    );
  }

  onBack(): void {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    void this.router.navigate(['/', RouteConstants.routeDeviceGroup]);
  }

  onLoadMoreHistory(): void {
    if (
      !this.currentGroupId ||
      this.historyLoading ||
      !this.canLoadMoreHistory
    ) {
      return;
    }

    this.currentPage += 1;
    this.fetchHistoryPage(this.currentPage, false);
  }

  async onCreateRule(): Promise<void> {
    await this.router.navigate([
      '/',
      RouteConstants.routeRule,
      RouteConstants.routeAdd,
    ]);
  }

  private loadInitialHistory(): void {
    if (!this.currentGroupId) {
      return;
    }

    this.currentPage = 0;
    this.fetchHistoryPage(0, true);
  }

  private fetchHistoryPage(page: number, replace: boolean): void {
    if (!this.currentGroupId) {
      return;
    }

    const requestSeq = ++this.historyRequestSeq;
    this.historyLoading = true;
    this.deviceGroupsApiService
      .getDeviceGroupHistory(this.currentGroupId, this.pageSize, page)
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          if (requestSeq === this.historyRequestSeq) {
            this.historyLoading = false;
          }
        }),
      )
      .subscribe((data) => {
        if (requestSeq !== this.historyRequestSeq) {
          return;
        }

        if (!data) {
          if (replace) {
            this.resetHistory();
          }
          return;
        }

        this.canLoadMoreHistory = data.history.length >= this.pageSize;
        const grouped = this.groupByDate(data.history);

        if (replace) {
          this.history = grouped;
          return;
        }

        this.history = this.mergeHistoryGroups(this.history, grouped);
      });
  }

  private resetHistory(): void {
    this.currentPage = 0;
    this.history = [];
    this.canLoadMoreHistory = false;
  }

  private groupByDate(
    items: ApiDeviceHistoryEntryResponseDto[],
  ): HistoryGroupViewModel[] {
    return Array.from(
      items.reduce((acc, item) => {
        const dateKey = new Date(item.eventDate).toDateString();
        if (!acc.has(dateKey)) {
          acc.set(dateKey, []);
        }
        acc.get(dateKey)!.push(item);
        return acc;
      }, new Map<string, ApiDeviceHistoryEntryResponseDto[]>()),
      ([date, groupItems]) => ({ date, items: groupItems }),
    );
  }

  private mergeHistoryGroups(
    existing: HistoryGroupViewModel[],
    incoming: HistoryGroupViewModel[],
  ): HistoryGroupViewModel[] {
    const map = new Map<string, ApiDeviceHistoryEntryResponseDto[]>();

    for (const group of existing) {
      map.set(group.date, [...group.items]);
    }

    for (const group of incoming) {
      const current = map.get(group.date) ?? [];
      map.set(group.date, [...current, ...group.items]);
    }

    return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
  }

  private toMapDevice(device: DeviceResponseDto): MapDevice {
    return {
      id: device.id,
      name: device.name,
      latitude: device.location?.lat,
      longitude: device.location?.lon,
      type: device.type,
      isFavorite: device.isFavorite,
      isOutOfService: device.isOutOfService,
    };
  }

  get activeDeviceCount(): number {
    return (
      this.currentDeviceGroup?.devices?.filter((device) =>
        isDeviceActive(device),
      ).length ?? 0
    );
  }

  get deviceCount(): number {
    return this.currentDeviceGroup?.devices?.length ?? 0;
  }

  get isPartiallyActive(): boolean {
    return this.currentDeviceGroup?.state === DeviceGroupState.PARTIALLY_ACTIVE;
  }
}
